/*
 * Copyright (C) 2019 - present by Potentially
 *
 * Please see distribution for license.
 */

import { BadgeShape, CompletionItemType } from '@app/app/shared/models/badge/models/badge.enum';
import { BadgeColorScheme } from '@app/app/shared/models/badge/models/badge.model';
import { SocialShareType } from '@app/app/shared/models/public-share/public-share.model';
import { LogicalOperator } from 'typescript';
import { EditorContent, LanguageValue } from '../../../shared/models';
import { LoadableState, defaultLoadableState } from '../../../shared/store';

export const INITIAL_FOLIO_STATE: FolioStateModel = {
  folio: defaultLoadableState(),
  displayHiddenItems: true,
  folioMomentsUpdate: true,
};

export interface FolioStateModel {
  folio: LoadableState<FolioModel>;
  displayHiddenItems: boolean;
  folioMomentsUpdate: boolean;
}

export interface FolioModel {
  _id: string;
  socialHandles: string[];
  publicId: string;
  moments: string[];
  isLive: boolean;
  learningHistory: string[];
  bio: FolioUserBio;
  badges: FolioBadges[];
  sections: FolioSection[];
  coverImageUrl: string;
}

export interface FolioUserBio {
  uid: string;
  firstName: string;
  lastName: string;
  introduction: string;
  email: string;
  organizationName: string;
  profileImage: string;
  userWorkspaceUid: string;
}

export interface FolioBadges {
  badgeId: string;
  badgeUid?: string;
  organizationId: string;
  imageUrl: string;
  issuedOn: Date;
  userBadgeUid?: string;
  issuedBy: string;
  orgLogo?: string;
  color?: string;
  title: LanguageValue[];
  userWorkspacesUid: string;
  visible: boolean;
  issuedBadge?: FolioBadges;
  isIndependent?: boolean;
  shape: BadgeShape;
  colorScheme?: BadgeColorScheme;
  completedCriteriaV2?: CompletedCriteriaV2;
}

export interface MemberFolioBadgeItem {
  issuedBadge: FolioBadges;
  visible: boolean;
}

export interface PublicFolioBadge {
  color: string;
  description: EditorContent[];
  imageUrl: string;
  issuedBy: string;
  issuedOn: Date;
  orgLogo: string;
  publisher: string;
  title: string;
  uid: string;
  userImageUrl: string;
  userName: string;
  liveFolio: boolean;
  publicFolioHandle: string;
  issuerDomain: string;
  completedCriteriaDetails: CompleteCriteria;
  userUid: string;
  isIndependent?: boolean;
  shape: BadgeShape;
  colorScheme?: BadgeColorScheme;
  completedCriteriaV2?: CompletedCriteriaV2;
  earningCriteriaItemsInfo?: Record<string, { title: string; imageUrl?: string }>;
}

export interface CompleteCriteria {
  headline: string;
  thumbnail: string;
  cardType: string;
  completedOn: Date;
  includedCards: CompleteCriteria[];
}

export type CompletedCriteriaV2 = CompletedExpression | CompletedMinimumPool | CompletedPoints | CompletedItem | CompletedWeightedItem;

export interface CompletedExpression {
  type: 'EXPRESSION';
  operator: LogicalOperator;
  children: CompletedCriteriaV2[];
}

export interface CompletedMinimumPool {
  type: 'MINIMUM_POOL';
  requiredCount: number;
  fulfilled: CompletedItem[];
}

export interface CompletedPoints {
  type: 'POINTS';
  requiredPoints: number;
  earnedPoints: number;
  fulfilled: CompletedWeightedItem[];
}

export interface CompletedItem {
  type: 'ITEM';
  itemId: string;
  itemType: CompletionItemType;
  completedOn: string;
}

export interface CompletedWeightedItem {
  type: 'WEIGHTED_ITEM';
  itemId: string;
  itemType: CompletionItemType;
  completedOn: string;
}

export enum FolioSectionType {
  GROWTH_TRACKER = 'GROWTH_TRACKER',
  MOMENTS = 'MOMENTS',
  PROJECTS = 'PROJECTS',
  ACHIEVEMENTS = 'ACHIEVEMENTS',
  EVENTS = 'EVENTS',
  PORTFOLIO = 'PORTFOLIO',
}

export enum FolioSectionItemType {
  GROWTH_TRACKER = 'tags',
  MOMENTS = 'moments',
  PROJECTS = 'projects',
  ACHIEVEMENTS = 'badges',
  CERTIFICATES = 'certificates',
  EVENTS = 'EVENTS',
}

export interface AchievementCertificateBlock {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  block?: any;
  uid: string;
  visible: boolean;
  name?: string;
}

export interface AchievementCertificate {
  filename: string | null;
  thumbnail: string | null;
  url: string | null;
  visible: boolean;
  uid: string;
  type: string;
  code: string;
}

export interface FolioSection {
  type: FolioSectionType;
  isVisible: boolean;
  isEditable: boolean;
  uid: string;
}

export const VALID_FOLIO_SECTIONS = [
  FolioSectionType.GROWTH_TRACKER,
  FolioSectionType.MOMENTS,
  FolioSectionType.ACHIEVEMENTS,
  FolioSectionType.PROJECTS,
  FolioSectionType.PORTFOLIO,
];

export type FolioSkillsGraph = 'BUBBLE' | 'SPIDER' | 'NONE';

export interface GrowthTrackerSection extends FolioSection {
  type: FolioSectionType.GROWTH_TRACKER;
  tags: string[];
  skillsGraph?: FolioSkillsGraph;
}

export interface FolioMomentsSection extends FolioSection {
  type: FolioSectionType.MOMENTS;
  moments: string[];
}

export interface FolioProjectsSection extends FolioSection {
  type: FolioSectionType.PROJECTS;
  projects: string[];
}

export interface FolioAchievementsSection extends FolioSection {
  type: FolioSectionType.ACHIEVEMENTS;
  badges: FolioBadges[];
  certificates: AchievementCertificateBlock[];
}

export interface FolioEventsSection extends FolioSection {
  type: FolioSectionType.EVENTS;
  events: string[];
}

export interface FolioShowcaseRequest {
  isLive?: boolean;
  shortDescription?: string;
  publicId?: string;
}

export interface FolioCoverUpdateRequest {
  imageName: string;
  contentLength: number;
}

export interface FolioSectionRequest {
  type?: FolioSectionType;
  isVisible?: boolean;
  uid?: string;
}

export interface GrowthTrackerSectionRequest extends FolioSectionRequest {
  type?: FolioSectionType.GROWTH_TRACKER;
  tags?: string[];
  skillsGraph?: FolioSkillsGraph;
}

export interface MomentsSectionRequest extends FolioSectionRequest {
  type?: FolioSectionType.MOMENTS;
  moments?: string[];
}

export interface ProjectsSectionRequest extends FolioSectionRequest {
  type?: FolioSectionType.PROJECTS;
  projects?: string[];
}

export interface AchievementsSectionRequest extends FolioSectionRequest {
  type?: FolioSectionType.ACHIEVEMENTS;
  badges?: FolioBadges[];
}

export interface UploadCertificateRequest {
  uid: string;
  dynamicContent: EditorContent[];
}

export interface FolioSectionItemRequest {
  type: FolioSectionItemType;
  uid: string;
}

export interface FolioMyActivity {
  template: string;
  tags: ActivityTag[];
  type: string;
  createdAt: Date;
}

export interface ActivityTag {
  id: string;
  title: string;
}

export interface FolioShareSocialItem {
  name: SocialShareType;
  title: string;
  icon?: string;
}

export interface SkillsSectionFilter {
  title: string;
  type: SkillsSectionFilterType;
}

export interface SkillsSectionSort {
  title: string;
  type: SkillsSectionSortType;
}

export enum SkillsSectionFilterType {
  FRAMEWORK = 'FRAMEWORK',
  ORGANIZATION = 'ORGANIZATION',
}

export enum SkillsSectionSortType {
  HIGHEST = 'HIGHEST',
  LOWEST = 'LOWEST',
}
